import React, { useState, useEffect } from 'react'
import { X, UserPlus, Search, Loader2 } from 'lucide-react'
import { EmployeeAvatar } from '@/components/vacation/EmployeeAvatar'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { useEmployeeAutocomplete } from '@/hooks/useWorkforce'
import { debounce } from '@/utils/debounce'

interface SelectedEmployee {
  employeeId: string
  name: string
  department?: string
  role?: string
}

interface CollaboratorSelectorProps {
  selectedEmployees: SelectedEmployee[]
  onSelectedChange: (employees: SelectedEmployee[]) => void
  year: number
}

export function CollaboratorSelector({
  selectedEmployees,
  onSelectedChange,
  year,
}: CollaboratorSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const debouncedUpdate = debounce((value: string) => {
      setDebouncedSearch(value)
    }, 300)
    debouncedUpdate(searchTerm)
    return () => debouncedUpdate.cancel()
  }, [searchTerm])

  const { suggestions, loading } = useEmployeeAutocomplete(debouncedSearch, {
    enabled: debouncedSearch.length >= 2,
    limit: 10,
  })

  const handleSelect = (suggestion: { employeeId: string; name: string; department?: string }) => {
    if (selectedEmployees.some(e => e.employeeId === suggestion.employeeId)) return
    onSelectedChange([
      ...selectedEmployees,
      {
        employeeId: suggestion.employeeId,
        name: suggestion.name,
        department: suggestion.department,
      },
    ])
    setSearchTerm('')
    setDebouncedSearch('')
  }

  const handleRemove = (employeeId: string) => {
    onSelectedChange(selectedEmployees.filter(e => e.employeeId !== employeeId))
  }

  const showDropdown = searchTerm.length >= 2 && (loading || suggestions.length > 0)

  return (
    <div className="space-y-4">
      {/* Inline search — no portal, works inside Dialog */}
      <div>
        <label className="block text-sm text-brand-gray mb-1">Buscar colaborador</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray pointer-events-none" />
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Digite nome ou email..."
            className="bg-brand-dark-lighter border-brand-gray/30 text-white pl-10 pr-9"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-brand-gray" />
          )}
        </div>

        {/* Inline dropdown — rendered in flow, not portaled */}
        {showDropdown && (
          <div className="mt-1 bg-brand-dark-lighter border border-brand-gray/30 rounded-monumenta shadow-lg max-h-[200px] overflow-y-auto">
            {loading && suggestions.length === 0 && (
              <div className="p-3 text-center text-sm text-brand-gray">Buscando...</div>
            )}
            {suggestions.map((s) => {
              const alreadySelected = selectedEmployees.some(e => e.employeeId === s.employeeId)
              return (
                <button
                  key={s.employeeId}
                  type="button"
                  disabled={alreadySelected}
                  onClick={() => handleSelect(s)}
                  className={`w-full px-3 py-2 flex items-center gap-3 text-left transition-colors ${
                    alreadySelected
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:bg-brand-gray/10 cursor-pointer'
                  }`}
                >
                  <EmployeeAvatar name={s.name} src={s.pictureUrl} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate">{s.name}</div>
                    {s.department && (
                      <span className="text-xs text-brand-gray">{s.department}</span>
                    )}
                  </div>
                  {alreadySelected && (
                    <span className="text-xs text-brand-gray">Adicionado</span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        {searchTerm.length > 0 && searchTerm.length < 2 && (
          <p className="mt-1 text-xs text-brand-gray">Digite pelo menos 2 caracteres</p>
        )}
      </div>

      {/* Selected list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-brand-gray">
            Selecionados ({selectedEmployees.length})
          </label>
          {selectedEmployees.length > 0 && (
            <button
              type="button"
              className="text-xs text-red-400 hover:text-red-300 transition-colors"
              onClick={() => onSelectedChange([])}
            >
              Limpar todos
            </button>
          )}
        </div>

        {selectedEmployees.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-brand-gray/30 rounded-monumenta">
            <UserPlus className="mx-auto h-8 w-8 text-brand-gray/40 mb-2" />
            <p className="text-sm text-brand-gray">
              Busque e selecione os colaboradores para o ciclo {year}
            </p>
          </div>
        ) : (
          <div className="space-y-1 max-h-[200px] overflow-y-auto">
            {selectedEmployees.map((emp) => (
              <div
                key={emp.employeeId}
                className="flex items-center gap-3 p-2 rounded-monumenta bg-brand-dark-lighter"
              >
                <EmployeeAvatar name={emp.name} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{emp.name}</div>
                  {emp.department && (
                    <span className="text-xs text-brand-gray">{emp.department}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="text-brand-gray hover:text-red-400 transition-colors p-1"
                  onClick={() => handleRemove(emp.employeeId)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
